import { isEmpty } from 'lodash/fp';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { pipe } from 'fp-ts/function';
import { formValueSelector } from 'redux-form';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { ApplicationState, CommanderThunkDispatch, Option, WithOwnerId, WithScope } from '@mews-commander/types';
import { mapAzureLocation, isObjectOrKeysEmpty } from '@mews-commander/utils';
import { getCountryOptions } from '@mews-commander/legacy-entities/src/ducks/countries';
import { getCountrySubdivisionOptions } from '@mews-commander/legacy-entities/src/ducks/countrySubdivisions';
import { FieldsContainer } from '@mews-commander/ui';
import { commanderForm, withAutoFocusingFirstFocusableElement, SelectField } from '@mews-commander/legacy-form';
import { AUTOCOMPLETE_NAME, AddressFormFields } from '../../../components/form';
import { getSelectedAddressFormValues } from '../selectors';
import { autocompleteAddress as autocompleteAddressAction, clearCountrySubdivision } from '../actions';
import { CommanderSidebar } from '../../../components/sidebar';
import { AddressFormValues } from '../types';

interface OwnProps extends WithScope, WithOwnerId {
    form: string;
}

interface StateProps {
    countryOptions: Array<Option>;
    countrySubdivisionsOptions: Array<Option>;
    initialValues: AddressFormValues | null;
}

interface DispatchProps {
    autocompleteAddress: (values: $TSFixMe) => void;
    handleClearCountrySubdivision: () => void;
}

interface Props extends OwnProps, StateProps, DispatchProps {
    onClose?: () => void;
}

const AddressForm = ({
    onClose,
    autocompleteAddress,
    countryOptions,
    countrySubdivisionsOptions,
    handleClearCountrySubdivision,
    form,
    ...other
}: Props) => {
    useEffect(() => {
        if (onClose) {
            return () => {
                onClose?.();
            };
        }
    }, [onClose]);

    const handleAddressAutocomplete = (place: $TSFixMe) => autocompleteAddress(mapAzureLocation(place));

    return (
        <FieldsContainer {...other}>
            <AddressFormFields
                countryOptions={countryOptions}
                onAutocomplete={handleAddressAutocomplete}
                onChange={handleClearCountrySubdivision}
                scope={form}
            />
            {!isEmpty(countrySubdivisionsOptions) && (
                <SelectField {...countrySubdivisionProps} options={countrySubdivisionsOptions} />
            )}
        </FieldsContainer>
    );
};

const countrySubdivisionProps = {
    label: <LocalizedText textKey={TextKey.CountrySubdivision} />,
    name: 'countrySubdivisionCode',
    filterable: true,
};

export default pipe(
    AddressForm,
    commanderForm({
        enableReinitialize: true,
        submitLabel: <LocalizedText textKey={TextKey.Save} />,
        validate: (values: $TSFixMe) => {
            const errors = {};

            if (isObjectOrKeysEmpty(values)) {
                // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type '"addressAutoComplete"' can't be used to index type '{}'...
                errors[AUTOCOMPLETE_NAME] = 'InvalidAddress';
            }

            return errors;
        },
        submitButtonProps: { variant: 'primary' },
    }),
    withAutoFocusingFirstFocusableElement(),
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        (state, { scope, form, ownerId }) => ({
            countryOptions: getCountryOptions(state),
            countrySubdivisionsOptions: getCountrySubdivisionOptions(state, {
                countryCode: formValueSelector(form)(state, 'countryCode'),
            }),
            initialValues: getSelectedAddressFormValues(state, { scope, ownerId }),
        }),
        (dispatch: CommanderThunkDispatch, { form }) => ({
            autocompleteAddress: (values) => dispatch(autocompleteAddressAction(form, values)),
            handleClearCountrySubdivision: () => dispatch(clearCountrySubdivision(form)),
        })
    ),
    CommanderSidebar
);
